import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {jwtDecode} from 'jwt-decode';
import {
    chatWebSocketUrl,
    createConversation as createConversationRequest,
    createWebSocketTicket,
    ensurePropertyChannel as ensurePropertyChannelRequest,
    getChatBootstrap,
    getConversation,
    getUnreadStatus,
    listChatUsers,
    listConversationFeed,
    listConversationGroups,
    queryConversationSummaries,
    syncEvents,
} from '../../service/ChatService';
import {
    chatCacheScope,
    clearChatCacheScope,
    deleteConversationCache,
    readCachedChat,
    readCachedGroups,
    readCachedMessages,
    readEventCursor,
    replaceEventCursor,
    writeBootstrapCache,
    writeCachedGroups,
    writeConversationCache,
    writeEventCursor,
    writeMessageCache,
} from './cache/chatCache';
import {getChatDeviceId} from './notifications/chatDevice';
import {useWebPushNotifications} from './notifications/useWebPushNotifications';
import {createJobChatStore} from './jobChatStore';
import {appendRealtimeBuffer} from './realtimeBuffer';
import {createRefreshQueue} from './refreshQueue';
import {createUnreadSnapshotState} from './unreadSnapshot';

const ChatContext = createContext(null);
function unreadStatusCount(payload) {
    // Preserve a minimal unread indication while older API/cache payloads lack a count.
    return Number.isSafeInteger(payload?.unread_count) && payload.unread_count >= 0
        ? payload.unread_count : payload?.has_unread ? 1 : 0;
}
const MAX_MUTATION_MESSAGES_PER_CONVERSATION = 64;
const MAX_MUTATION_CONVERSATIONS = 50;

function mutationEventRank(event) {
    return Number(event?.event_id || 0) || new Date(event?.occurred_at || 0).getTime();
}

export function mergeBoundedMessageMutations(current, incoming) {
    const next = {...current};
    incoming.forEach((events, conversationId) => {
        const merged = {...next[conversationId], ...Object.fromEntries(events)};
        const retained = Object.entries(merged)
            .toSorted((left, right) => mutationEventRank(right[1]) - mutationEventRank(left[1]))
            .slice(0, MAX_MUTATION_MESSAGES_PER_CONVERSATION);
        next[conversationId] = Object.fromEntries(retained);
    });
    return Object.fromEntries(Object.entries(next)
        .toSorted((left, right) => {
            const newest = values => Math.max(0, ...Object.values(values).map(mutationEventRank));
            return newest(right[1]) - newest(left[1]);
        })
        .slice(0, MAX_MUTATION_CONVERSATIONS));
}

function tokenIdentity() {
    const token = localStorage.getItem('cosToken');
    if (!token) return '';
    try {
        const claims = jwtDecode(token.replace(/^Bearer\s+/i, ''));
        const apiUser = claims?.optinestUser?.optinestApiUser;
        return String(apiUser?.id || claims?.user_id || claims?.sub || apiUser?.username || claims?.username || '').toLowerCase();
    } catch {
        return '';
    }
}

function routeConversationId() {
    const match = window.location.pathname.match(/^\/chat\/(\d+)\/?$/);
    return Number(match?.[1] || 0);
}

function mergeConversationList(current, incoming) {
    const byId = new Map(current.map(item => [item.id, item]));
    for (const item of incoming || []) {
        const previous = byId.get(item.id);
        byId.set(item.id, previous?.details_loaded && !item.details_loaded
            ? {...previous, ...item, members: previous.members, context: previous.context, details_loaded: true}
            : {...previous, ...item});
    }
    return [...byId.values()].toSorted((left, right) => {
        const updated = new Date(right.updated_at || 0).getTime() - new Date(left.updated_at || 0).getTime();
        return updated || right.id - left.id;
    });
}

function mergeConversationDetails(current, details, preserveCurrentSummary) {
    if (!preserveCurrentSummary) return {...current, ...details, details_loaded: true};
    return {
        ...current,
        members: details.members,
        member_count: details.member_count,
        context: details.context,
        details_loaded: true,
        can_post: details.can_post,
        post_blocked_reason: details.post_blocked_reason,
    };
}

export function reconcileConversationSnapshot(current, incoming, watermark, watermarks, barriers) {
    const existing = new Map(current.map(item => [item.id, item]));
    return incoming.flatMap(item => {
        if (watermark < Math.max(watermarks.get(item.id) || 0, barriers.get(item.id) || 0)) return existing.has(item.id) ? [existing.get(item.id)] : [];
        watermarks.set(item.id, watermark);
        return [item];
    });
}

function chunked(values, size) {
    const chunks = [];
    for (let index = 0; index < values.length; index += size) chunks.push(values.slice(index, index + size));
    return chunks;
}

export function ChatProvider({children}) {
    const baseTitle = useRef(document.title);
    return <AccountChatProvider key={tokenIdentity()} baseTitle={baseTitle.current}>{children}</AccountChatProvider>;
}

function AccountChatProvider({children, baseTitle}) {
    const scopeRef = useRef(chatCacheScope(tokenIdentity()));
    const [users, setUsers] = useState([]);
    const [adminUsers, setAdminUsers] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [conversations, setConversations] = useState([]);
    const [conversationGroups, setConversationGroups] = useState([]);
    const [connectionStatus, setConnectionStatus] = useState('connecting');
    const [error, setError] = useState(null);
    const [inboxRevision, setInboxRevision] = useState(0);
    const [invalidations, setInvalidations] = useState({});
    const [messageMutationEvents, setMessageMutationEvents] = useState({});
    const [unreadCount, setUnreadCount] = useState(0);
    const [unreadStatus, setUnreadStatus] = useState('unavailable');
    const hasUnread = unreadCount > 0;
    const unreadRequestRef = useRef(0);
    const unreadSnapshot = useMemo(createUnreadSnapshotState, []);
    const summaryWatermarks = useRef(new Map());
    const readBarriers = useRef(new Map());
    const currentUserRef = useRef(currentUser);
    currentUserRef.current = currentUser;
    const baseTitleRef = useRef(baseTitle);
    const mountedRef = useRef(true);
    useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

    useEffect(() => {
        const baseTitle = baseTitleRef.current;
        document.title = unreadCount > 0 ? `(${unreadCount}) ${baseTitle}` : baseTitle;
        return () => { document.title = baseTitle; };
    }, [unreadCount]);
    const [nextCursor, setNextCursor] = useState('');
    const [hasMoreConversations, setHasMoreConversations] = useState(false);
    const conversationsRef = useRef([]);
    const conversationDetailPromisesRef = useRef(new Map());
    const conversationGroupsLoadedRef = useRef(false);
    const conversationGroupsPromiseRef = useRef(null);
    const bootstrapPromiseRef = useRef(null);
    const summaryRefreshTimerRef = useRef(null);
    const pendingSummaryIdsRef = useRef(new Set());
    const permissionRefreshIdsRef = useRef(new Set());
    const summaryRequestGenerationRef = useRef(new Map());
    const socketRef = useRef(null);
    const jobChat = useMemo(() => createJobChatStore(frame => {
        const socket = socketRef.current;
        if (!socket || socket.readyState !== WebSocket.OPEN) return false;
        socket.send(JSON.stringify(frame)); return true;
    }), []);
    const activeConversationRef = useRef(null);
    const notifications = useWebPushNotifications();

    const updateConversations = useCallback(incoming => {
        if (!incoming?.length) return;
        setConversations(current => {
            const next = mergeConversationList(current, incoming);
            conversationsRef.current = next;
            return next;
        });
        void writeConversationCache(scopeRef.current, incoming);
    }, []);

    const replaceConversations = useCallback(incoming => {
        const nextItems = incoming || [];
        const incomingIds = new Set(nextItems.map(item => item.id));
        const removedIds = conversationsRef.current
            .map(item => item.id)
            .filter(id => !incomingIds.has(id));
        setConversations(current => {
            const next = mergeConversationList(
                current.filter(item => incomingIds.has(item.id)),
                nextItems,
            );
            conversationsRef.current = next;
            return next;
        });
        void writeConversationCache(scopeRef.current, nextItems);
        if (removedIds.length) void deleteConversationCache(scopeRef.current, removedIds);
    }, []);

    const removeConversations = useCallback(ids => {
        if (!ids?.length) return;
        const removed = new Set(ids.map(Number));
        setConversations(current => {
            const next = current.filter(item => !removed.has(item.id));
            conversationsRef.current = next;
            return next;
        });
        void deleteConversationCache(scopeRef.current, ids);
    }, []);

    const setActiveConversationId = useCallback(conversationId => {
        activeConversationRef.current = Number(conversationId) || null;
        const socket = socketRef.current;
        if (!socket || socket.readyState !== WebSocket.OPEN) return;
        socket.send(JSON.stringify({
            type: 'presence.update',
            app_state: document.visibilityState === 'visible' ? 'FOREGROUND' : 'BACKGROUND',
            current_conversation_id: activeConversationRef.current,
        }));
    }, []);

    const searchDirectory = useCallback(async ({query, cursor, signal} = {}) => {
        const payload = await listChatUsers(signal, {
            scope: 'collaboration', query, cursor, limit: 50,
        });
        setUsers(current => cursor
            ? [...new Map([...current, ...(payload.users || [])].map(user => [user.id, user])).values()]
            : (payload.users || []));
        setCurrentUser(current => payload.current_user || current);
        return payload;
    }, []);

    const refreshDirectory = useCallback((signal, options = {}) => (
        searchDirectory({...options, signal})
    ), [searchDirectory]);

    const refreshAdminDirectory = useCallback(async signal => {
        const payload = await listChatUsers(signal);
        setAdminUsers(payload.users || []);
        setCurrentUser(current => payload.current_user || current);
        return payload;
    }, []);

    const unreadQueue = useMemo(() => createRefreshQueue(async (_, signal) => {
        try {
            const payload = await getUnreadStatus(signal);
            if (!signal.aborted && mountedRef.current && unreadSnapshot.apply(payload)) {
                setUnreadCount(unreadStatusCount(payload));
                setUnreadStatus(unreadSnapshot.isStale() ? 'stale' : 'ready');
            }
            return Boolean(payload?.has_unread);
        } catch (failure) {
            if (mountedRef.current && (!signal.aborted || signal.reason?.name === 'TimeoutError')) setUnreadStatus('unavailable');
            throw failure;
        }
    }, (_, latest) => latest, {minIntervalMs: 5000, retry: true}), [unreadSnapshot]);
    const refreshUnreadStatus = useCallback(signal => {
        unreadSnapshot.invalidate();
        return unreadQueue.request(++unreadRequestRef.current, signal);
    }, [unreadQueue, unreadSnapshot]);

    useEffect(() => {
        const timer = setInterval(() => {
            if (unreadSnapshot.isStale()) setUnreadStatus('stale');
            window.dispatchEvent(new CustomEvent('chat-metric', {detail: {name: 'unread_freshness', lag_ms: unreadSnapshot.freshnessMs(), stale: unreadSnapshot.isStale()}}));
            if (unreadSnapshot.isDirty() && document.visibilityState === 'visible') {
                void refreshUnreadStatus().catch(() => undefined);
            }
        }, 1000);
        return () => clearInterval(timer);
    }, [refreshUnreadStatus, unreadSnapshot]);

    const executeSummaryRefresh = useCallback(async (ids, signal) => {
        const unique = [...new Set((ids || []).map(Number).filter(Boolean))];
        const refreshed = [];
        for (const batch of chunked(unique, 100)) {
            const generations = new Map(batch.map(id => {
                const generation = (summaryRequestGenerationRef.current.get(id) || 0) + 1;
                summaryRequestGenerationRef.current.set(id, generation);
                return [id, generation];
            }));
            const payload = await queryConversationSummaries(batch, signal);
            if (signal.aborted) return refreshed;
            const watermark = Number(payload.snapshot_event_id) || 0;
            const acceptedId = id => generations.get(Number(id)) === summaryRequestGenerationRef.current.get(Number(id))
                && watermark >= Math.max(summaryWatermarks.current.get(Number(id)) || 0, readBarriers.current.get(Number(id)) || 0);
            const accepted = (payload.conversations || []).filter(item => acceptedId(item.id));
            const unavailable = (payload.unavailable_ids || []).filter(acceptedId);
            [...accepted.map(item => item.id), ...unavailable].forEach(id => summaryWatermarks.current.set(Number(id), watermark));
            jobChat.updateSummaries(accepted);
            updateConversations(accepted);
            removeConversations(unavailable);
            refreshed.push(...accepted);
        }
        const activeId = activeConversationRef.current;
        if (activeId && permissionRefreshIdsRef.current.delete(activeId)) {
            try {
                const detail = await getConversation(activeId, signal);
                const current = conversationsRef.current.find(item => item.id === activeId);
                if (!signal.aborted && current) updateConversations([mergeConversationDetails(current, detail, true)]);
            } catch (failure) {
                if (failure.status === 403 || failure.status === 404) removeConversations([activeId]);
                else { permissionRefreshIdsRef.current.add(activeId); throw failure; }
            }
        }
        return refreshed;
    }, [jobChat, removeConversations, updateConversations]);

    const summaryQueue = useMemo(() => createRefreshQueue(executeSummaryRefresh,
        (left, right) => [...new Set([...left, ...right])], {minIntervalMs: 1000, retry: true}), [executeSummaryRefresh]);
    const queryAndMergeSummaries = useCallback((ids, signal) => summaryQueue.request(ids, signal), [summaryQueue]);
    useEffect(() => () => { unreadQueue.cancel(); summaryQueue.cancel(); }, [unreadQueue, summaryQueue]);

    const refreshConversations = useCallback(async signal => {
        const ids = [...new Set([...conversationsRef.current.slice(0, 50).map(item => item.id), activeConversationRef.current].filter(Boolean))];
        if (!ids.length) {
            return bootstrapPromiseRef.current || [];
        }
        void refreshUnreadStatus(signal).catch(() => undefined);
        return queryAndMergeSummaries(ids, signal);
    }, [queryAndMergeSummaries, refreshUnreadStatus]);

    const loadMoreConversations = useCallback(async signal => {
        if (!hasMoreConversations || !nextCursor) return [];
        const payload = await listConversationFeed({cursor: nextCursor, signal});
        const accepted = reconcileConversationSnapshot(conversationsRef.current, payload.conversations || [], Number(payload.snapshot_event_id) || 0, summaryWatermarks.current, readBarriers.current);
        updateConversations(accepted);
        setNextCursor(payload.next_cursor || '');
        setHasMoreConversations(Boolean(payload.has_more));
        return payload.conversations || [];
    }, [hasMoreConversations, nextCursor, updateConversations]);

    const searchConversations = useCallback(async ({query, agentKey, cursor, signal} = {}) => {
        const payload = await listConversationFeed({query, agentKey, cursor, signal});
        const conversations = reconcileConversationSnapshot(conversationsRef.current, payload.conversations || [], Number(payload.snapshot_event_id) || 0, summaryWatermarks.current, readBarriers.current);
        updateConversations(conversations);
        return {...payload, conversations};
    }, [updateConversations]);

    const loadConversationGroups = useCallback(signal => {
        if (conversationGroupsLoadedRef.current) return Promise.resolve(conversationGroups);
        if (conversationGroupsPromiseRef.current) return conversationGroupsPromiseRef.current;
        const request = (async () => {
            const cached = await readCachedGroups(scopeRef.current);
            if (cached.length) setConversationGroups(cached);
            const payload = await listConversationGroups(signal);
            const groups = payload.groups || [];
            setConversationGroups(groups);
            conversationGroupsLoadedRef.current = true;
            void writeCachedGroups(scopeRef.current, groups);
            return groups;
        })().finally(() => {
            if (conversationGroupsPromiseRef.current === request) {
                conversationGroupsPromiseRef.current = null;
            }
        });
        conversationGroupsPromiseRef.current = request;
        return request;
    }, [conversationGroups]);

    const flushSummaryRefresh = useCallback(async signal => {
        const ids = [...pendingSummaryIdsRef.current];
        pendingSummaryIdsRef.current.clear();
        if (!ids.length) return;
        try {
            await queryAndMergeSummaries(ids, signal);
        } catch (refreshError) {
            if (refreshError?.name !== 'AbortError') setError(refreshError);
        }
    }, [queryAndMergeSummaries]);

    const scheduleSummaryRefresh = useCallback((conversationId, signal) => {
        const id = Number(conversationId) || 0;
        if (!id) return;
        pendingSummaryIdsRef.current.add(id);
        if (summaryRefreshTimerRef.current) return;
        summaryRefreshTimerRef.current = window.setTimeout(() => {
            summaryRefreshTimerRef.current = null;
            void flushSummaryRefresh(signal);
        }, 100);
    }, [flushSummaryRefresh]);

    useEffect(() => jobChat.subscribeSummaries(items => {
        // Legacy Job WebSocket counts have no snapshot watermark. Reconcile
        // affected loaded rooms through the authoritative snapshot endpoint.
        const loaded = new Set(conversationsRef.current.map(item => item.id));
        items.forEach(item => { if (loaded.has(item.id)) scheduleSummaryRefresh(item.id); });
    }), [jobChat, scheduleSummaryRefresh]);

    useEffect(() => {
        let stopped = false;
        let socket = null;
        let reconnectTimer = null;
        let retry = 0;
        let retryAfterAt = 0;
        let connectionGeneration = 0;
        let connectionAbortController = null;
        const abortController = new AbortController();
        const scope = scopeRef.current;
        const selectedId = routeConversationId();
        let networkBootstrapApplied = false;
        let networkUnreadUnavailable = false;
        let lastBootstrap = null;
        let bootstrapError = null;

        const executeBootstrap = async ({
            clear = false,
            signal = abortController.signal,
            shouldApply = () => !stopped,
        } = {}) => {
            const unreadGeneration = ++unreadRequestRef.current;
            const payload = await getChatBootstrap({selectedId, signal});
            if (!shouldApply()) return payload;
            if (clear) {
                await clearChatCacheScope(scope);
                if (!shouldApply()) return payload;
                conversationsRef.current = [];
                setConversations([]);
                setConversationGroups([]);
                unreadSnapshot.reset();
                readBarriers.current.clear();
                permissionRefreshIdsRef.current.clear();
                summaryWatermarks.current.clear();
                conversationGroupsLoadedRef.current = false;
            }
            networkBootstrapApplied = true;
            networkUnreadUnavailable = payload.unread_status === 'unavailable';
            lastBootstrap = payload;
            setError(current => current === bootstrapError ? null : current);
            if (networkUnreadUnavailable) unreadSnapshot.invalidate();
            const summaryWatermark = Number(payload.conversation_snapshot_event_id) || 0;
            const incoming = reconcileConversationSnapshot(conversationsRef.current, payload.conversations || [], summaryWatermark, summaryWatermarks.current, readBarriers.current);
            if (payload.selected_conversation) {
                const selected = payload.selected_conversation;
                const current = incoming.find(item => item.id === selected.id) || conversationsRef.current.find(item => item.id === selected.id);
                if (current) incoming.push(mergeConversationDetails(current, selected, true));
                else if (!(readBarriers.current.get(selected.id) > summaryWatermark)) incoming.push(selected);
            }
            replaceConversations(incoming);
            setCurrentUser(payload.current_user || null);
            setNextCursor(payload.next_cursor || '');
            setHasMoreConversations(Boolean(payload.has_more));
            if (unreadGeneration === unreadRequestRef.current) {
                if (payload.unread_status !== 'unavailable' && unreadSnapshot.apply(payload)) setUnreadCount(unreadStatusCount(payload));
                setUnreadStatus(payload.unread_status || 'ready');
            }
            void writeBootstrapCache(scope, payload);
            return payload;
        };

        const bootstrapQueue = createRefreshQueue(async (options, signal) => {
            try { return await executeBootstrap({...options, signal}); }
            catch (failure) { if (failure?.name !== 'AbortError') { bootstrapError = failure; setError(failure); } throw failure; }
        }, (_, latest) => latest, {retry: true});
        const loadBootstrap = (options = {}) => bootstrapQueue.request(options, options.signal || abortController.signal);

        const cachedPromise = readCachedChat(scope, {selectedId}).then(cached => {
            if (stopped) return cached;
            if (networkBootstrapApplied && networkUnreadUnavailable && unreadRequestRef.current <= 1) {
                setUnreadCount(unreadStatusCount(cached.meta));
            }
            if (!networkBootstrapApplied) {
                const incoming = [...cached.conversations];
                if (cached.selected) incoming.push(cached.selected);
                replaceConversations(incoming);
                if (cached.meta?.current_user) setCurrentUser(cached.meta.current_user);
                if (unreadRequestRef.current <= 1) {
                    setUnreadCount(unreadStatusCount(cached.meta));
                    setUnreadStatus(cached.meta?.unread_status || 'unavailable');
                }
                setNextCursor(cached.meta?.next_cursor || '');
                setHasMoreConversations(Boolean(cached.meta?.has_more));
            }
            return cached;
        });
        void readCachedGroups(scope).then(groups => {
            if (!stopped && groups.length) setConversationGroups(groups);
        });
        bootstrapPromiseRef.current = Promise.all([cachedPromise, loadBootstrap()])
            .then(([, payload]) => payload)
            .catch(initialError => {
                if (!stopped && initialError?.name !== 'AbortError') setError(initialError);
                return null;
            });

        const processEvents = incoming => {
            if (incoming?.length) setInboxRevision(value => value + 1);
            const revoked = new Set();
            const invalidationCounts = new Map();
            const mutations = new Map();
            for (const event of incoming || []) {
                if (!event?.event_id) continue;
                jobChat.event(event);
                const affectsUnread = !['message.reaction_added', 'message.reaction_removed', 'reaction.added', 'reaction.removed', 'message.updated'].includes(event.type);
                if (event.type === 'conversation.read') {
                    unreadSnapshot.confirmRead(event.event_id);
                    readBarriers.current.set(Number(event.conversation_id), Math.max(readBarriers.current.get(Number(event.conversation_id)) || 0, Number(event.event_id)));
                } else if (affectsUnread) unreadSnapshot.invalidate(event.event_id);
                if (affectsUnread) void refreshUnreadStatus(abortController.signal).catch(() => undefined);
                if (event.type === 'conversation.access_revoked' && event.conversation_id) {
                    revoked.add(event.conversation_id);
                    scheduleSummaryRefresh(event.conversation_id, abortController.signal);
                    continue;
                }
                if (!event.conversation_id) continue;
                if (['membership.added', 'membership.removed', 'conversation.updated', 'conversation.assignment_changed'].includes(event.type)) {
                    if (event.conversation_id === activeConversationRef.current) permissionRefreshIdsRef.current.add(event.conversation_id);
                }
                if (event.type === 'message.created') invalidationCounts.set(event.conversation_id, (invalidationCounts.get(event.conversation_id) || 0) + 1);
                const loaded = conversationsRef.current.some(item => item.id === event.conversation_id);
                const opensChannel = ['conversation.created', 'membership.added', 'conversation.unarchived', 'conversation.assignment_changed'].includes(event.type);
                if (affectsUnread && (loaded || event.conversation_id === activeConversationRef.current || opensChannel)) {
                    scheduleSummaryRefresh(event.conversation_id, abortController.signal);
                }
                const messageId = Number(event.data?.message_id || 0);
                if (Number.isSafeInteger(messageId) && messageId > 0) {
                    const conversationMutations = mutations.get(event.conversation_id) || new Map();
                    conversationMutations.set(messageId, event);
                    mutations.set(event.conversation_id, conversationMutations);
                }
            }
            if (revoked.size) removeConversations([...revoked]);
            if (invalidationCounts.size) {
                setInvalidations(current => {
                    const next = {...current};
                    invalidationCounts.forEach((count, conversationId) => {
                        next[conversationId] = (next[conversationId] || 0) + count;
                    });
                    return next;
                });
            }
            if (mutations.size) {
                setMessageMutationEvents(current => mergeBoundedMessageMutations(current, mutations));
            }
        };

        const confirmedRead = ({detail}) => {
            if (Number(detail?.actor_id) !== Number(currentUserRef.current?.id)) return;
            setInboxRevision(value => value + 1);
            unreadSnapshot.confirmRead(detail.event_id);
            const id = Number(detail.conversation_id);
            jobChat.event({type: 'conversation.read', conversation_id: id, event_id: detail.event_id});
            readBarriers.current.set(id, Math.max(readBarriers.current.get(id) || 0, Number(detail.event_id) || 0));
            if (Number.isSafeInteger(detail.last_read_root_sequence)) {
                const room = conversationsRef.current.find(item => item.id === id);
                if (room) updateConversations([{...room, last_read_root_sequence: Math.max(room.last_read_root_sequence || 0, detail.last_read_root_sequence)}]);
            }
            scheduleSummaryRefresh(id, abortController.signal);
            void refreshUnreadStatus(abortController.signal).catch(() => undefined);
        };
        window.addEventListener('chat-read-confirmed', confirmedRead);
        const processEvent = event => processEvents([event]);

        const catchUp = async (watermark, connectionSignal, isCurrentConnection) => {
            // Establish the bounded REST baseline before applying any event
            // summary refresh. Otherwise a slower, older bootstrap response can
            // overwrite a newer summary produced by catch-up.
            const bootstrap = lastBootstrap || await bootstrapPromiseRef.current;
            if (!bootstrap) throw new Error('Chat is waiting for its initial snapshot');
            if (!isCurrentConnection()) throw new DOMException('Connection replaced', 'AbortError');
            let cursor = await readEventCursor(scope);
            if (!isCurrentConnection()) throw new DOMException('Connection replaced', 'AbortError');
            let barrier = watermark;
            let reset = false;
            if (cursor === 0) {
                cursor = Number(bootstrap?.sync_cursor || 0);
            }
            if (cursor > barrier) {
                // A persisted cursor ahead of the server watermark means the event
                // store was rewound or the local cache belongs to an obsolete
                // baseline. Rebuild immediately instead of silently accepting a
                // cursor that would skip future events.
                reset = true;
                cursor = barrier;
            }
            while (cursor < barrier && isCurrentConnection()) {
                const page = await syncEvents(cursor, barrier, connectionSignal);
                if (!isCurrentConnection()) throw new DOMException('Connection replaced', 'AbortError');
                if (page.reset_required) {
                    reset = true;
                    barrier = Number(page.next_event_id ?? barrier);
                    cursor = barrier;
                    break;
                }
                processEvents([...(page.events || [])].toSorted((left, right) => left.event_id - right.event_id));
                const next = Number(page.next_event_id || cursor);
                if (next <= cursor) break;
                cursor = next;
                if (!page.has_more) break;
            }
            if (reset) {
                await loadBootstrap({clear: true, signal: connectionSignal, shouldApply: isCurrentConnection});
                if (!isCurrentConnection()) throw new DOMException('Connection replaced', 'AbortError');
            }
            return {cursor, barrier, reset};
        };

        const connect = async () => {
            if (stopped) return;
            const generation = ++connectionGeneration;
            connectionAbortController?.abort();
            const connectionController = new AbortController();
            connectionAbortController = connectionController;
            const isCurrentConnection = () => !stopped
                && generation === connectionGeneration
                && !connectionController.signal.aborted;
            setConnectionStatus(retry === 0 ? 'connecting' : 'reconnecting');
            try {
                const allocation = await createWebSocketTicket(getChatDeviceId(), connectionController.signal);
                if (!isCurrentConnection()) return;
                const buffered = [];
                let bufferedBytes = 0;
                let syncing = true;
                let bufferOverflowed = false;
                let cursor = 0;
                const currentSocket = new WebSocket(chatWebSocketUrl(), [
                    'cos-chat.v1',
                    `cos-chat-ticket.${allocation.ticket}`,
                ]);
                socket = currentSocket;
                socketRef.current = currentSocket;
                currentSocket.onmessage = async message => {
                    if (!isCurrentConnection()) return;
                    let frame;
                    try {
                        frame = JSON.parse(message.data);
                    } catch {
                        return;
                    }
                    if (jobChat.receive(frame)) return;
                    if (frame.type === 'connection.ready') {
                        setConnectionStatus('live');
                        setActiveConversationId(activeConversationRef.current);
                        try {
                            const caughtUp = await catchUp(
                                Number(frame.data?.sync_through_event_id || 0),
                                connectionController.signal,
                                isCurrentConnection,
                            );
                            if (!isCurrentConnection()) return;
                            // A socket alone is not recovery: failed Sync must
                            // retain its backoff until catch-up succeeds.
                            retry = 0;
                            retryAfterAt = 0;
                            setError(null);
                            cursor = caughtUp.cursor;
                            syncing = false;
                            jobChat.setConnected(true);
                            const bufferedEvents = buffered.splice(0)
                                .filter(item => Number(item.event_id) > cursor)
                                .toSorted((left, right) => left.event_id - right.event_id);
                            if (bufferedEvents.length) {
                                processEvents(bufferedEvents);
                                cursor = bufferedEvents.at(-1).event_id;
                            }
                            bufferedBytes = 0;
                            cursor = Math.max(cursor, caughtUp.barrier);
                            const persistence = caughtUp.reset
                                ? replaceEventCursor(scope, cursor)
                                : writeEventCursor(scope, cursor);
                            void Promise.resolve(persistence).catch(persistenceError => {
                                if (isCurrentConnection()) setError(persistenceError);
                            });
                        } catch (catchUpError) {
                            if (isCurrentConnection() && catchUpError?.name !== 'AbortError') {
                                retryAfterAt = Date.now() + (catchUpError.retryAfterMs || 0);
                                setError(catchUpError);
                                currentSocket.close();
                            }
                        }
                        return;
                    }
                    if (syncing) {
                        const result = appendRealtimeBuffer(
                            buffered,
                            frame,
                            bufferedBytes,
                            typeof message.data === 'string' ? message.data.length * 2 : 0,
                        );
                        if (result.overflow && !bufferOverflowed) {
                            bufferOverflowed = true;
                            buffered.length = 0;
                            bufferedBytes = 0;
                            syncing = false;
                            connectionController.abort();
                            void replaceEventCursor(scope, 0);
                            bootstrapPromiseRef.current = loadBootstrap({clear: true, signal: abortController.signal})
                                .catch(resetError => {
                                    if (!stopped && resetError?.name !== 'AbortError') setError(resetError);
                                    return null;
                                });
                            currentSocket.close();
                        } else {
                            bufferedBytes = result.bufferedBytes;
                        }
                        return;
                    }
                    if (frame.event_id > cursor) {
                        processEvent(frame);
                        cursor = frame.event_id;
                        void writeEventCursor(scope, cursor);
                    }
                };
                currentSocket.onclose = () => {
                    if (generation === connectionGeneration) jobChat.setConnected(false);
                    if (generation !== connectionGeneration) return;
                    connectionGeneration += 1;
                    connectionController.abort();
                    if (socketRef.current === currentSocket) socketRef.current = null;
                    if (socket === currentSocket) socket = null;
                    if (stopped) return;
                    const delay = Math.max(retryAfterAt - Date.now(), Math.min(15000, 1000 * 2 ** retry) + Math.floor(Math.random() * 500));
                    retry += 1;
                    setConnectionStatus('reconnecting');
                    reconnectTimer = window.setTimeout(connect, delay);
                };
                currentSocket.onerror = () => currentSocket.close();
            } catch (connectError) {
                if (!isCurrentConnection()) return;
                connectionGeneration += 1;
                connectionController.abort();
                setError(connectError);
                retryAfterAt = Date.now() + (connectError.retryAfterMs || 0);
                const delay = Math.max(retryAfterAt - Date.now(), Math.min(15000, 1000 * 2 ** retry) + Math.floor(Math.random() * 500));
                retry += 1;
                setConnectionStatus('reconnecting');
                reconnectTimer = window.setTimeout(connect, delay);
            }
        };

        void connect();
        const handleOnline = () => {
            if (socket?.readyState === WebSocket.OPEN || socket?.readyState === WebSocket.CONNECTING) return;
            window.clearTimeout(reconnectTimer);
            retry = 0;
            void connect();
        };
        const handleVisibility = () => {
            setActiveConversationId(activeConversationRef.current);
            if (document.visibilityState === 'visible') {
                void refreshUnreadStatus(abortController.signal).catch(refreshError => {
                    if (refreshError?.name !== 'AbortError') setError(refreshError);
                });
            }
        };
        window.addEventListener('online', handleOnline);
        document.addEventListener('visibilitychange', handleVisibility);
        return () => {
            stopped = true;
            window.removeEventListener('chat-read-confirmed', confirmedRead);
            bootstrapQueue.cancel();
            connectionGeneration += 1;
            connectionAbortController?.abort();
            abortController.abort();
            window.clearTimeout(reconnectTimer);
            window.clearTimeout(summaryRefreshTimerRef.current);
            window.removeEventListener('online', handleOnline);
            document.removeEventListener('visibilitychange', handleVisibility);
            jobChat.dispose();
            socketRef.current = null;
            socket?.close(1000, 'page closed');
        };
    }, [jobChat, removeConversations, replaceConversations, scheduleSummaryRefresh, setActiveConversationId, updateConversations, refreshUnreadStatus, unreadSnapshot]);

    const createConversation = useCallback(async payload => {
        const response = await createConversationRequest(payload);
        if (response.conversation) updateConversations([response.conversation]);
        return response;
    }, [updateConversations]);

    const ensurePropertyChannel = useCallback(async (propertyId, cosJobId, signal) => {
        const result = await ensurePropertyChannelRequest(propertyId, cosJobId, signal);
        if (result?.conversation) updateConversations([result.conversation]);
        return result;
    }, [updateConversations]);

    const loadConversation = useCallback((conversationId, signal, {force = false} = {}) => {
        const id = Number(conversationId) || 0;
        if (!id) return Promise.resolve(null);
        const existing = conversationsRef.current.find(item => item.id === id);
        if (existing?.details_loaded && !force) return Promise.resolve(existing);
        const inFlight = conversationDetailPromisesRef.current.get(id);
        if (inFlight) return inFlight;
        const summaryGeneration = summaryRequestGenerationRef.current.get(id) || 0;
        const readBarrier = readBarriers.current.get(id) || 0;
        const requestedScope = scopeRef.current;
        const request = getConversation(id, signal).then(payload => {
            const fullConversation = {...payload, can_post: payload.can_post, post_blocked_reason: payload.post_blocked_reason};
            const currentConversation = conversationsRef.current.find(item => item.id === id);
            if (!mountedRef.current || signal?.aborted || scopeRef.current !== requestedScope) throw new DOMException('Chat session changed', 'AbortError');
            if (!currentConversation) { updateConversations([fullConversation]); return fullConversation; }
            const mergedConversation = mergeConversationDetails(
                currentConversation,
                fullConversation,
                (summaryRequestGenerationRef.current.get(id) || 0) !== summaryGeneration || (readBarriers.current.get(id) || 0) !== readBarrier,
            );
            const next = conversationsRef.current.map(item => item.id === id ? mergedConversation : item);
            conversationsRef.current = next;
            setConversations(next);
            void writeConversationCache(scopeRef.current, [mergedConversation]);
            return fullConversation;
        }).finally(() => {
            if (conversationDetailPromisesRef.current.get(id) === request) {
                conversationDetailPromisesRef.current.delete(id);
            }
        });
        conversationDetailPromisesRef.current.set(id, request);
        return request;
    }, [updateConversations]);

    const readConversationMessages = useCallback(conversationId => (
        readCachedMessages(scopeRef.current, conversationId)
    ), []);
    const cacheConversationMessages = useCallback((conversationId, messages) => (
        writeMessageCache(scopeRef.current, conversationId, messages)
    ), []);

    const value = useMemo(() => ({
        jobChat,
        users,
        adminUsers,
        currentUser,
        conversations,
        conversationGroups,
        connectionStatus,
        error,
        setError,
        inboxRevision,
        invalidations,
        messageMutationEvents,
        notifications,
        setActiveConversationId,
        hasUnread,
        unreadCount,
        unreadStatus,
        cacheScope: scopeRef.current,
        hasMoreConversations,
        refreshDirectory,
        refreshAdminDirectory,
        searchDirectory,
        refreshConversations,
        refreshUnreadStatus,
        loadMoreConversations,
        searchConversations,
        loadConversationGroups,
        loadConversation,
        createConversation,
        ensurePropertyChannel,
        readConversationMessages,
        cacheConversationMessages,
    }), [jobChat, users, adminUsers, currentUser, conversations, conversationGroups, connectionStatus, error,
        inboxRevision, invalidations, messageMutationEvents, notifications, hasUnread, unreadCount, unreadStatus, hasMoreConversations, refreshDirectory,
        refreshAdminDirectory, searchDirectory,
        refreshConversations, refreshUnreadStatus, loadMoreConversations, searchConversations,
        loadConversationGroups, loadConversation, createConversation, ensurePropertyChannel,
        readConversationMessages, cacheConversationMessages, setActiveConversationId]);

    return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat() {
    const value = useContext(ChatContext);
    if (!value) throw new Error('useChat must be used within ChatProvider');
    return value;
}
